import React, { useState, useEffect, useRef, useContext } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
    Card, CardBody, Row, Col, Form, FormGroup, Label, Input, Table
} from 'reactstrap'
import Select from 'react-select'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'
import { numberFormat } from '../../helpers/Currency'
import {
    distributePayment,
    recalculateRow,
    buildComplementoPayload,
    validateComplemento
} from '../../helpers/complementoHelpers'
import { formaPagoValores, MonedaValores } from '../../data/catalogs/CatalogosSAT'
import ApiService from '../../data/services/ApiService'
import Breadcrumbs from '../../components/Global/Breadcrumbs'
import { Container } from 'reactstrap'
import { GeneralContext } from '../../context/'
import Modal from '../../components/Modal'

const today = () => new Date().toISOString().split('T')[0]

const fmt = (n) => `$${numberFormat(parseFloat(n) || 0, 2, '.', ',')}`

const NuevoComplemento = () => {
    const history = useHistory()
    const location = useLocation()
    const { setLoading } = useContext(GeneralContext)

    // ── Payment header ────────────────────────────────────────────────────────
    const [header, setHeader] = useState({
        clienteId:          '',
        fechaPago:          today(),
        formaPago:          null,
        moneda:             'MXN',
        tipoCambio:         1,
        monto:              0,
        numeroOperacion:    '',
        rfcEmisorCuentaOrd: '',
        nombreBancoOrdExt:  '',
        cuentaOrdenante:    '',
        rfcEmisorCtaBen:    '',
        cuentaBeneficiario: ''
    })

    // ── Clientes — raw for ID lookup, options for Select ──────────────────────
    const [rawClientes, setRawClientes]         = useState([])
    const [clienteOptions, setClienteOptions]   = useState([])

    // ── Modal ─────────────────────────────────────────────────────────────────
    const [isModalOpen, setIsModalOpen]               = useState(false)
    const [allFacturasPendientes, setAllFacturas]     = useState([])
    const [modalSearch, setModalSearch]               = useState('')
    const [modalClienteFilter, setModalClienteFilter] = useState(null)
    const [modalLoadingFacturas, setModalLoading]     = useState(false)
    const [modalPage, setModalPage]                   = useState(1)
    const [modalTotalPages, setModalTotalPages]       = useState(1)
    const [modalTotal, setModalTotal]                 = useState(0)
    const [includePastYears, setIncludePastYears]     = useState(false)
    const debounceRef                                 = useRef(null)
    const preselectedInvoiceLoadedRef                 = useRef(false)

    // ── PUE allocation modal ─────────────────────────────────────────────────
    const [isPueModalOpen, setIsPueModalOpen]             = useState(false)
    const [pueFacturas, setPueFacturas]                   = useState([])
    const [pueAllocations, setPueAllocations]             = useState([])
    const [pueSearch, setPueSearch]                       = useState('')
    const [pueLoadingFacturas, setPueLoadingFacturas]     = useState(false)
    const [puePage, setPuePage]                           = useState(1)
    const [pueTotalPages, setPueTotalPages]               = useState(1)
    const [pueTotal, setPueTotal]                         = useState(0)
    const pueDebounceRef                                  = useRef(null)

    // ── Added invoices + distribution rows ────────────────────────────────────
    const [addedInvoices, setAddedInvoices] = useState([])
    const [rows, setRows]                   = useState([])

    // ── Confirm-change-customer dialog ────────────────────────────────────────
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
    const [pendingInvoice, setPendingInvoice]         = useState(null)

    // ── Submit ────────────────────────────────────────────────────────────────
    const [submitting, setSubmitting] = useState(false)

    // ── Load clientes on mount ────────────────────────────────────────────────
    useEffect(() => {
        ApiService.getCustomers().then(({ data }) => {
            if (data?.data) {
                setRawClientes(data.data)
                setClienteOptions(
                    data.data.map(c => ({
                        value: c._id,
                        label: `${c.razonSocial} — ${c.rfc}`
                    }))
                )
            }
        })
    }, [])

    // ── Modal helpers ─────────────────────────────────────────────────────────
    const fetchModalFacturas = async (page, clienteId, search, pastYears) => {
        setModalLoading(true)
        try {
            const params = { page, limit: 20 }
            if (clienteId)  params.clienteId       = clienteId
            if (search)     params.search           = search
            if (pastYears)  params.includePastYears = true
            const { data } = await ApiService.getFacturasPendientes(params)
            setAllFacturas(data?.data || [])
            setModalPage(data?.pagination?.page || page)
            setModalTotalPages(data?.pagination?.totalPages || 1)
            setModalTotal(data?.pagination?.total || 0)
        } catch (_) {
            toastr.error('No pude cargar las facturas pendientes')
        } finally {
            setModalLoading(false)
        }
    }

    const openModal = async () => {
        setIsModalOpen(true)
        setModalSearch('')
        setModalClienteFilter(null)
        setIncludePastYears(false)
        await fetchModalFacturas(1, null, '', false)
    }

    const onModalSearchChange = (e) => {
        const val = e.target.value
        setModalSearch(val)
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            fetchModalFacturas(1, modalClienteFilter?.value || null, val, includePastYears)
        }, 400)
    }

    const onModalClienteChange = (option) => {
        setModalClienteFilter(option)
        fetchModalFacturas(1, option?.value || null, modalSearch, includePastYears)
    }

    const onTogglePastYears = () => {
        const next = !includePastYears
        setIncludePastYears(next)
        fetchModalFacturas(1, modalClienteFilter?.value || null, modalSearch, next)
    }

    const _closeModal = () => {
        setIsModalOpen(false)
    }

    const addedInvoiceIds = new Set(addedInvoices.map(inv => String(inv._id)))
    const pueAllocationIds = new Set(pueAllocations.map(a => String(a.facturaId)))

    // ── Rows enriched with receptorId (distributePayment doesn’t include it) ───────
    const buildRows = (invoices, monto) => {
        const idMap = new Map(
            invoices.map(inv => [
                String(inv._id),
                inv.receptor?._id ? String(inv.receptor._id) : null
            ])
        )
        return distributePayment(invoices, monto).map(r => ({
            ...r,
            receptorId: idMap.get(String(r.facturaId)) || null
        }))
    }

    // ── Derived current receptor ──────────────────────────────────────────────
    const currentReceptorId = rows.length > 0 ? rows[0].receptorId : null

    // ── Commit an invoice (internal — always safe to call) ────────────────────
    const _commitInvoice = (factura, baseInvoices) => {
        const newInvoices = [...baseInvoices, factura]
        setAddedInvoices(newInvoices)
        setRows(buildRows(newInvoices, parseFloat(header.monto) || 0))

        if (!header.clienteId) {
            if (modalClienteFilter) {
                setHeader(prev => ({ ...prev, clienteId: modalClienteFilter.value }))
            } else if (factura.receptor?.rfc) {
                const match = rawClientes.find(c => c.rfc === factura.receptor.rfc)
                if (match) setHeader(prev => ({ ...prev, clienteId: String(match._id) }))
            }
        }
    }

    // Invoice actions can enter this form with a PPD invoice already selected.
    useEffect(() => {
        const facturaId = location.state?.facturaId
        if (!facturaId || preselectedInvoiceLoadedRef.current) return
        preselectedInvoiceLoadedRef.current = true

        ;(async () => {
            try {
                setLoading(true)
                const response = await ApiService.getFactura(facturaId)
                const factura = response.data?.data?.factura
                const cliente = response.data?.data?.cliente
                if (!factura || factura.metodoPago !== 'PPD' || factura.pagada === true) {
                    toastr.error('La factura seleccionada no está disponible para generar un complemento.')
                    return
                }
                const selected = {
                    ...factura,
                    receptor: cliente ? {
                        _id: cliente._id,
                        razonSocial: cliente.razonSocial,
                        rfc: cliente.rfc
                    } : null,
                    saldoPendiente: factura.saldoPendiente != null
                        ? factura.saldoPendiente
                        : (parseFloat(factura.total) || 0) - (parseFloat(factura.montoPagado) || 0)
                }
                const monto = parseFloat(selected.saldoPendiente) || 0
                setAddedInvoices([selected])
                setRows(buildRows([selected], monto))
                setHeader(prev => ({
                    ...prev,
                    clienteId: cliente?._id ? String(cliente._id) : '',
                    moneda: factura.moneda || 'MXN',
                    monto
                }))
            } catch (error) {
                toastr.error(error.response?.data?.message || 'No pude cargar la factura seleccionada.')
            } finally {
                setLoading(false)
            }
        })()
        // The navigation state is intentionally consumed once on mount.
    }, [])

    // ── Add / remove invoice ──────────────────────────────────────────────────
    const addInvoice = (factura) => {
        const incomingReceptorId = factura.receptor?._id
            ? String(factura.receptor._id)
            : null

        if (currentReceptorId && incomingReceptorId && currentReceptorId !== incomingReceptorId) {
            // Different customer — ask for confirmation
            setPendingInvoice(factura)
            setIsConfirmModalOpen(true)
            return
        }

        _commitInvoice(factura, addedInvoices)
    }

    const confirmChangeCustomer = () => {
        if (!pendingInvoice) return
        setAddedInvoices([])
        setRows([])
        setPueAllocations([])
        setHeader(prev => ({ ...prev, clienteId: '' }))
        _commitInvoice(pendingInvoice, [])
        setPendingInvoice(null)
        setIsConfirmModalOpen(false)
    }

    const cancelChangeCustomer = () => {
        setPendingInvoice(null)
        setIsConfirmModalOpen(false)
    }

    const removeInvoice = (facturaId) => {
        const newInvoices = addedInvoices.filter(inv => String(inv._id) !== String(facturaId))
        setAddedInvoices(newInvoices)
        setRows(buildRows(newInvoices, parseFloat(header.monto) || 0))
        if (newInvoices.length === 0) {
            setPueAllocations([])
        }
    }

    // ── Header / row change handlers ────────────────────────────────────────────
    const onMontoChange = (e) => {
        const monto = parseFloat(e.target.value) || 0
        setHeader(prev => ({ ...prev, monto }))
        setRows(buildRows(addedInvoices, monto))
    }

    const onImportePagadoChange = (idx, rawValue) => {
        setRows(prev => recalculateRow(prev, idx, rawValue))
    }

    // ── Running totals ────────────────────────────────────────────────────────
    const totalDistribuido = rows.reduce((acc, r) => acc + (parseFloat(r.importePagado) || 0), 0)
    const totalNoComplementable = pueAllocations.reduce((acc, r) => acc + (parseFloat(r.importeAplicado) || 0), 0)
    const totalAsignado = totalDistribuido + totalNoComplementable
    const pendienteAsignar = (parseFloat(header.monto) || 0) - totalAsignado
    const asignacionOk = Math.abs(pendienteAsignar) <= 0.01

    const fetchPueFacturas = async (page, search) => {
        const clienteId = header.clienteId || currentReceptorId
        if (!clienteId) {
            toastr.error('Selecciona primero una factura PPD para determinar el cliente')
            return
        }

        setPueLoadingFacturas(true)
        try {
            const params = {
                page,
                limit: 20,
                clienteId,
                fechaPago: header.fechaPago
            }
            if (search) params.search = search
            const { data } = await ApiService.getFacturasNoComplementables(params)
            setPueFacturas(data?.data || [])
            setPuePage(data?.pagination?.page || page)
            setPueTotalPages(data?.pagination?.totalPages || 1)
            setPueTotal(data?.pagination?.total || 0)
        } catch (err) {
            toastr.error(err?.response?.data?.message || 'No pude cargar las facturas PUE')
        } finally {
            setPueLoadingFacturas(false)
        }
    }

    const openPueModal = async () => {
        if (rows.length === 0) {
            toastr.error('Agrega primero al menos una factura PPD')
            return
        }
        setIsPueModalOpen(true)
        setPueSearch('')
        await fetchPueFacturas(1, '')
    }

    const closePueModal = () => {
        setIsPueModalOpen(false)
    }

    const onPueSearchChange = (e) => {
        const val = e.target.value
        setPueSearch(val)
        clearTimeout(pueDebounceRef.current)
        pueDebounceRef.current = setTimeout(() => {
            fetchPueFacturas(1, val)
        }, 400)
    }

    const addPueAllocation = (factura) => {
        const pendiente = Math.max(0, (parseFloat(header.monto) || 0) - totalDistribuido - totalNoComplementable)
        const disponible = parseFloat(factura.importeDisponibleNoComplementable ?? factura.total) || 0
        const importeAplicado = Math.min(pendiente, disponible)

        if (!factura.fechaPagoValida) {
            toastr.error(factura.validationMessage || 'La factura PUE está fuera del periodo válido')
            return
        }
        if (importeAplicado <= 0) {
            toastr.error('No hay importe pendiente para asignar a esta factura PUE')
            return
        }

        setPueAllocations(prev => [
            ...prev,
            {
                facturaId: factura._id,
                folio: factura.folio,
                uuid: factura.uuid,
                total: factura.total,
                pagada: factura.pagada,
                fecha: factura.fecha,
                receptor: factura.receptor,
                importeDisponibleNoComplementable: disponible,
                fechaPagoValida: factura.fechaPagoValida,
                validationMessage: factura.validationMessage,
                importeAplicado
            }
        ])
    }

    const removePueAllocation = (facturaId) => {
        setPueAllocations(prev => prev.filter(a => String(a.facturaId) !== String(facturaId)))
    }

    const onPueImporteChange = (facturaId, rawValue) => {
        const importeAplicado = parseFloat(rawValue) || 0
        setPueAllocations(prev => prev.map(a => (
            String(a.facturaId) === String(facturaId)
                ? { ...a, importeAplicado }
                : a
        )))
    }

    // Lookup map so the table can display receptor/fecha without a find() per render
    const invById = new Map(addedInvoices.map(inv => [String(inv._id), inv]))

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        const { valid, errors } = validateComplemento(header, addedInvoices, rows, pueAllocations)
        if (!valid) {
            errors.forEach(e => toastr.error(e))
            return
        }

        const payload = buildComplementoPayload(header, addedInvoices, rows, pueAllocations)

        setSubmitting(true)
        setLoading(true)
        try {
            const { data } = await ApiService.timbrarComplemento(payload)
            if (data?.success) {
                toastr.success(data.message || 'Complemento timbrado correctamente')
                history.push('/complementos')
            } else {
                toastr.error(data?.message || 'No se pudo timbrar el complemento')
            }
        } catch (err) {
            toastr.error(
                err?.response?.data?.message ||
                'No pude conectarme al servidor. Inténtalo de nuevo.'
            )
        } finally {
            setSubmitting(false)
            setLoading(false)
        }
    }

    const submitDisabled = submitting || !asignacionOk || addedInvoices.length === 0

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <React.Fragment>
            <div id="mainContent" className="page mainContent">
                <Container fluid>
                    <Breadcrumbs
                        title="Complementos de Pago"
                        icon="fa fa-file-invoice-dollar"
                        breadcrumbItem="Nuevo Complemento de Pago"
                        parentLink="/complementos"
                    />

                    {/* ── Card 3: Submit ── */}
                    <Card>
                        <CardBody>
                            <Row>
                                <Col className="text-right">
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-lg"
                                        disabled={submitDisabled}
                                        onClick={handleSubmit}
                                    >
                                        {submitting ? 'Timbrando…' : 'Timbrar Complemento'}
                                    </button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {/* ── Card 1: Datos del Pago ── */}
                    <Card>
                        <CardBody>
                            <h5 className="mb-3">Datos del Pago</h5>
                            <Form>
                                <Row>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Fecha de Pago *</Label>
                                            <Input
                                                type="date"
                                                value={header.fechaPago}
                                                onChange={e => {
                                                    const value = e.target.value
                                                    setHeader(prev => ({ ...prev, fechaPago: value }))
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Forma de Pago *</Label>
                                            <Select
                                                options={formaPagoValores}
                                                value={formaPagoValores.find(o => o.value === header.formaPago) ?? null}
                                                onChange={opt => setHeader(prev => ({ ...prev, formaPago: opt?.value || '' }))}
                                                placeholder="Selecciona..."
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Moneda *</Label>
                                            <Select
                                                options={MonedaValores}
                                                value={MonedaValores.find(o => o.value === header.moneda) ?? null}
                                                onChange={opt => setHeader(prev => ({ ...prev, moneda: opt?.value || 'MXN' }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    {header.moneda !== 'MXN' && header.moneda !== 'XXX' && (
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label>Tipo de Cambio *</Label>
                                                <Input
                                                    type="number"
                                                    step="0.0001"
                                                    value={header.tipoCambio}
                                                    onChange={e => setHeader(prev => ({
                                                        ...prev,
                                                        tipoCambio: parseFloat(e.target.value) || 0
                                                    }))}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>
                                <Row>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Monto del Pago *</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={header.monto || ''}
                                                onChange={onMontoChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Número de Operación</Label>
                                            <Input
                                                type="text"
                                                value={header.numeroOperacion}
                                                onChange={e => {
                                                    const value = e.target.value
                                                    setHeader(prev => ({ ...prev, numeroOperacion: value }))
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>RFC Emisor Cta. Ord.</Label>
                                            <Input
                                                type="text"
                                                value={header.rfcEmisorCuentaOrd}
                                                onChange={e => {
                                                    const value = e.target.value
                                                    setHeader(prev => ({ ...prev, rfcEmisorCuentaOrd: value }))
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Nombre Banco Ord. Ext.</Label>
                                            <Input
                                                type="text"
                                                value={header.nombreBancoOrdExt}
                                                onChange={e => {
                                                    const value = e.target.value
                                                    setHeader(prev => ({ ...prev, nombreBancoOrdExt: value }))
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Cuenta Ordenante</Label>
                                            <Input
                                                type="text"
                                                value={header.cuentaOrdenante}
                                                onChange={e => {
                                                    const value = e.target.value
                                                    setHeader(prev => ({ ...prev, cuentaOrdenante: value }))
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>RFC Emisor Cta. Ben.</Label>
                                            <Input
                                                type="text"
                                                value={header.rfcEmisorCtaBen}
                                                onChange={e => {
                                                    const value = e.target.value
                                                    setHeader(prev => ({ ...prev, rfcEmisorCtaBen: value }))
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Cuenta Beneficiario</Label>
                                            <Input
                                                type="text"
                                                value={header.cuentaBeneficiario}
                                                onChange={e => {
                                                    const value = e.target.value
                                                    setHeader(prev => ({ ...prev, cuentaBeneficiario: value }))
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>

                    {/* ── Card 2: Documentos Relacionados ── */}
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">Documentos Relacionados</h5>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={openModal}
                                >
                                    <i className="fa fa-plus mr-1" />
                                    Agregar Factura
                                </button>
                            </div>

                            {rows.length === 0 ? (
                                <p className="text-muted text-center py-3">
                                    Haz clic en + para agregar facturas a este complemento
                                </p>
                            ) : (
                                <>
                                    <Table responsive bordered size="sm">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 40 }}></th>
                                                <th>Folio</th>
                                                <th>Cliente</th>
                                                <th>Fecha</th>
                                                <th className="text-right">Saldo Pendiente</th>
                                                <th className="text-right" style={{ minWidth: 140 }}>Importe Pagado</th>
                                                <th className="text-right">Saldo Insoluto</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((row, idx) => {
                                                const inv = invById.get(String(row.facturaId))
                                                return (
                                                    <tr key={String(row.facturaId)}>
                                                        <td className="text-center align-middle">
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm text-danger p-0"
                                                                onClick={() => removeInvoice(row.facturaId)}
                                                                title="Quitar"
                                                            >
                                                                <i className="fa fa-times" />
                                                            </button>
                                                        </td>
                                                        <td className="align-middle">{row.folio || '—'}</td>
                                                        <td className="align-middle">{inv?.receptor?.razonSocial || '—'}</td>
                                                        <td className="align-middle">
                                                            {inv?.fecha
                                                                ? new Date(inv.fecha).toLocaleDateString('es-MX')
                                                                : '—'}
                                                        </td>
                                                        <td className="text-right align-middle">{fmt(row.importeSaldoAnterior)}</td>
                                                        <td className="text-right align-middle">
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                bsSize="sm"
                                                                value={row.importePagado}
                                                                onChange={e => onImportePagadoChange(idx, e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="text-right align-middle">{fmt(row.importeSaldoInsoluto)}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </Table>

                                    {/* Running totals */}
                                    <Row className="mt-3 justify-content-end">
                                        <Col md={4}>
                                            <Table size="sm" borderless>
                                                <tbody>
                                                    <tr>
                                                        <td>Monto del pago:</td>
                                                        <td className="text-right font-weight-bold">{fmt(header.monto)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Total PPD en XML:</td>
                                                        <td className="text-right font-weight-bold">{fmt(totalDistribuido)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Total PUE interno:</td>
                                                        <td className="text-right font-weight-bold">{fmt(totalNoComplementable)}</td>
                                                    </tr>
                                                    <tr style={{ color: asignacionOk ? 'green' : 'red' }}>
                                                        <td><strong>Pendiente de asignar:</strong></td>
                                                        <td className="text-right font-weight-bold">{fmt(pendienteAsignar)}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                            {pendienteAsignar > 0.01 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm btn-block"
                                                    onClick={openPueModal}
                                                >
                                                    <i className="fa fa-link mr-1" />
                                                    Asignar a factura PUE
                                                </button>
                                            )}
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </CardBody>
                    </Card>

                    {pueAllocations.length > 0 && (
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h5 className="mb-0">Asignaciones PUE</h5>
                                        <small className="text-muted">No incluido en el XML del complemento</small>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={openPueModal}
                                    >
                                        <i className="fa fa-plus mr-1" />
                                        Agregar PUE
                                    </button>
                                </div>

                                <Table responsive bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 40 }}></th>
                                            <th>Folio</th>
                                            <th>Fecha</th>
                                            <th>Estado</th>
                                            <th className="text-right">Total</th>
                                            <th className="text-right" style={{ minWidth: 140 }}>Importe asignado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pueAllocations.map(a => {
                                            const willMarkPaid = a.pagada !== true &&
                                                (parseFloat(a.importeAplicado) || 0) >= (parseFloat(a.total) || 0) - 0.01
                                            return (
                                                <tr key={String(a.facturaId)}>
                                                    <td className="text-center align-middle">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm text-danger p-0"
                                                            onClick={() => removePueAllocation(a.facturaId)}
                                                            title="Quitar"
                                                        >
                                                            <i className="fa fa-times" />
                                                        </button>
                                                    </td>
                                                    <td className="align-middle">{a.folio || '—'}</td>
                                                    <td className="align-middle">
                                                        {a.fecha ? new Date(a.fecha).toLocaleDateString('es-MX') : '—'}
                                                    </td>
                                                    <td className="align-middle">
                                                        {a.pagada === true ? (
                                                            <span className="badge badge-success">Pagada</span>
                                                        ) : willMarkPaid ? (
                                                            <span className="badge badge-info">Se marcará pagada</span>
                                                        ) : (
                                                            <span className="badge badge-warning">Pendiente</span>
                                                        )}
                                                    </td>
                                                    <td className="text-right align-middle">{fmt(a.total)}</td>
                                                    <td className="text-right align-middle">
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            bsSize="sm"
                                                            value={a.importeAplicado}
                                                            onChange={e => onPueImporteChange(a.facturaId, e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    )}
                </Container>
            </div>

            {/* ── Invoice search modal ── */}
            {isModalOpen && (
                <Modal
                    isOpen={isModalOpen}
                    onRequestClose={_closeModal}
                    title="Seleccionar documentos relacionados"
                    contentLabel="Agregar Facturas"
                    body={
                        <>
                            {/* Filters row */}
                            <Row className="mb-2">
                                <Col md={6}>
                                    <Input
                                        placeholder="Buscar por folio..."
                                        value={modalSearch}
                                        onChange={onModalSearchChange}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Select
                                        options={clienteOptions}
                                        value={modalClienteFilter}
                                        onChange={onModalClienteChange}
                                        placeholder="Filtrar por cliente..."
                                        isClearable
                                    />
                                </Col>
                            </Row>

                            {/* Past-years toggle / banner */}
                            <Row className="mb-3 align-items-center">
                                <Col>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="chkPastYears"
                                            checked={includePastYears}
                                            onChange={onTogglePastYears}
                                        />
                                        <label className="form-check-label" htmlFor="chkPastYears">
                                            Incluir años anteriores
                                        </label>
                                    </div>
                                    {includePastYears && (
                                        <span className="badge badge-info ml-2">
                                            Mostrando facturas de todos los años
                                        </span>
                                    )}
                                </Col>
                                {!modalLoadingFacturas && (
                                    <Col className="text-right text-muted" style={{ fontSize: '0.85em' }}>
                                        {modalTotal} {modalTotal === 1 ? 'resultado' : 'resultados'}
                                    </Col>
                                )}
                            </Row>

                            {/* Results */}
                            <Table hover size="sm" responsive>
                                <thead>
                                    <tr>
                                        <th>Folio</th>
                                        <th>Cliente</th>
                                        <th>Fecha</th>
                                        <th className="text-right">Total</th>
                                        <th className="text-right">Saldo Pendiente</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalLoadingFacturas ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <tr key={i}>
                                                {Array.from({ length: 5 }).map((__, j) => (
                                                    <td key={j}>
                                                        <div
                                                            className="bg-light rounded"
                                                            style={{ height: 16, width: '80%', animation: 'pulse 1.5s ease-in-out infinite' }}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : allFacturasPendientes.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted py-3">
                                                No se encontraron facturas pendientes de pago
                                            </td>
                                        </tr>
                                    ) : (
                                        allFacturasPendientes.map(f => {
                                            const alreadyAdded = addedInvoiceIds.has(String(f._id))
                                            const saldo        = parseFloat(f.saldoPendiente) || parseFloat(f.total) || 0
                                            const isEstimated  = f.saldoPendiente === null || f.saldoPendiente === undefined
                                            return (
                                                <tr
                                                    key={f._id}
                                                    onClick={() => !alreadyAdded && addInvoice(f)}
                                                    style={{
                                                        cursor:  alreadyAdded ? 'not-allowed' : 'pointer',
                                                        opacity: alreadyAdded ? 0.45 : 1
                                                    }}
                                                >
                                                    <td>{f.folio || '—'}</td>
                                                    <td>{f.receptor?.razonSocial || '—'}</td>
                                                    <td>
                                                        {f.fecha
                                                            ? new Date(f.fecha).toLocaleDateString('es-MX')
                                                            : '—'}
                                                    </td>
                                                    <td className="text-right">{fmt(f.total)}</td>
                                                    <td className="text-right">
                                                        {fmt(saldo)}
                                                        {isEstimated && (
                                                            <span
                                                                className="badge badge-secondary ml-1"
                                                                style={{ fontSize: '0.65em', verticalAlign: 'middle' }}
                                                            >
                                                                Saldo estimado
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </Table>

                            {/* Pagination */}
                            {modalTotalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={modalPage <= 1 || modalLoadingFacturas}
                                        onClick={() => fetchModalFacturas(
                                            modalPage - 1,
                                            modalClienteFilter?.value || null,
                                            modalSearch,
                                            includePastYears
                                        )}
                                    >
                                        ← Anterior
                                    </button>
                                    <span className="text-muted" style={{ fontSize: '0.85em' }}>
                                        Página {modalPage} de {modalTotalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={modalPage >= modalTotalPages || modalLoadingFacturas}
                                        onClick={() => fetchModalFacturas(
                                            modalPage + 1,
                                            modalClienteFilter?.value || null,
                                            modalSearch,
                                            includePastYears
                                        )}
                                    >
                                        Siguiente →
                                    </button>
                                </div>
                            )}
                        </>
                    }
                />
            )}

            {/* ── PUE allocation modal ── */}
            {isPueModalOpen && (
                <Modal
                    isOpen={isPueModalOpen}
                    onRequestClose={closePueModal}
                    title="Asignar pago a factura PUE"
                    contentLabel="Asignar Factura PUE"
                    body={
                        <>
                            <Row className="mb-2">
                                <Col md={7}>
                                    <Input
                                        placeholder="Buscar por folio..."
                                        value={pueSearch}
                                        onChange={onPueSearchChange}
                                    />
                                </Col>
                                <Col md={5} className="text-right">
                                    <div className="text-muted" style={{ fontSize: '0.85em' }}>
                                        Pendiente de asignar: <strong>{fmt(pendienteAsignar)}</strong>
                                    </div>
                                    {!pueLoadingFacturas && (
                                        <div className="text-muted" style={{ fontSize: '0.85em' }}>
                                            {pueTotal} {pueTotal === 1 ? 'resultado' : 'resultados'}
                                        </div>
                                    )}
                                </Col>
                            </Row>

                            <Table hover size="sm" responsive>
                                <thead>
                                    <tr>
                                        <th>Folio</th>
                                        <th>Fecha</th>
                                        <th>Estado</th>
                                        <th className="text-right">Total</th>
                                        <th className="text-right">Disponible</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pueLoadingFacturas ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <tr key={i}>
                                                {Array.from({ length: 5 }).map((__, j) => (
                                                    <td key={j}>
                                                        <div
                                                            className="bg-light rounded"
                                                            style={{ height: 16, width: '80%', animation: 'pulse 1.5s ease-in-out infinite' }}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : pueFacturas.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted py-3">
                                                No se encontraron facturas PUE para este cliente
                                            </td>
                                        </tr>
                                    ) : (
                                        pueFacturas.map(f => {
                                            const alreadyAdded = pueAllocationIds.has(String(f._id))
                                            const available = parseFloat(f.importeDisponibleNoComplementable) || 0
                                            const canAdd = !alreadyAdded && f.fechaPagoValida !== false && available > 0 && pendienteAsignar > 0.01

                                            return (
                                                <tr
                                                    key={f._id}
                                                    onClick={() => canAdd && addPueAllocation(f)}
                                                    title={f.validationMessage || ''}
                                                    style={{
                                                        cursor: canAdd ? 'pointer' : 'not-allowed',
                                                        opacity: canAdd ? 1 : 0.45
                                                    }}
                                                >
                                                    <td>{f.folio || '—'}</td>
                                                    <td>
                                                        {f.fecha
                                                            ? new Date(f.fecha).toLocaleDateString('es-MX')
                                                            : '—'}
                                                    </td>
                                                    <td>
                                                        {f.pagada === true ? (
                                                            <span className="badge badge-success">Pagada</span>
                                                        ) : (
                                                            <span className="badge badge-warning">Pendiente</span>
                                                        )}
                                                        {f.fechaPagoValida === false && (
                                                            <span className="badge badge-danger ml-1">
                                                                Fuera de periodo PUE
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-right">{fmt(f.total)}</td>
                                                    <td className="text-right">{fmt(available)}</td>
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </Table>

                            {pueTotalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={puePage <= 1 || pueLoadingFacturas}
                                        onClick={() => fetchPueFacturas(puePage - 1, pueSearch)}
                                    >
                                        ← Anterior
                                    </button>
                                    <span className="text-muted" style={{ fontSize: '0.85em' }}>
                                        Página {puePage} de {pueTotalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={puePage >= pueTotalPages || pueLoadingFacturas}
                                        onClick={() => fetchPueFacturas(puePage + 1, pueSearch)}
                                    >
                                        Siguiente →
                                    </button>
                                </div>
                            )}
                        </>
                    }
                />
            )}

            {/* ── Confirm change-customer dialog ── */}
            {isConfirmModalOpen && (
                <Modal
                    isOpen={isConfirmModalOpen}
                    onRequestClose={cancelChangeCustomer}
                    title="¿Cambiar cliente?"
                    contentLabel="Cambiar cliente"
                    body={
                        <p>
                            Las facturas que ya agregaste pertenecen a{' '}
                            <strong>{addedInvoices[0]?.receptor?.razonSocial || 'el cliente actual'}</strong>.
                            <br /><br />
                            Si agregas esta factura, se eliminarán las facturas seleccionadas y
                            comenzarás de nuevo con{' '}
                            <strong>{pendingInvoice?.receptor?.razonSocial || 'el nuevo cliente'}</strong>.
                            <br /><br />
                            ¿Deseas continuar?
                        </p>
                    }
                    footer={
                        <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={cancelChangeCustomer}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={confirmChangeCustomer}
                            >
                                Sí, cambiar cliente
                            </button>
                        </div>
                    }
                />
            )}
        </React.Fragment>
    )
}

export default NuevoComplemento
